/**
 * 运行时配置出口。
 *
 * 真正的「链 / 代币」数据都在 ./chains.js（单一真源），本文件负责：
 *   1) 依据 .env 选择启用哪些链（VUE_APP_CHAINS / VUE_APP_DEFAULT_CHAIN）；
 *   2) 把注册表转换成历史沿用的导出名（Network / ChainIdMap / *_CONTRACT ...），
 *      从而**完全兼容**既有组件，无需改动业务代码即可实现多链与多代币。
 *
 * 兼容说明：以前本文件固定 import './constants.prod'，无法按环境切换，
 * 也无法新增链/代币；现在改为注册表驱动。
 */
import BigNumber from 'bignumber.js';
import { CHAIN_REGISTRY, buildNetworkMap, toHexChainId } from './chains';

const ETHDecimal = 18;
const MaxUint256 = BigNumber(
  '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
).toFixed(0);

const allChains = Object.values(CHAIN_REGISTRY);

// 1) 默认启用注册表中 enabled = true 的链
let activeChains = allChains.filter((c) => c.enabled);

// 2) 允许通过 .env 覆盖：VUE_APP_CHAINS=bsc,bscTest
const envChains = (process.env.VUE_APP_CHAINS || '')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);
if (envChains.length) {
  const picked = envChains.map((k) => CHAIN_REGISTRY[k]).filter(Boolean);
  if (picked.length) {
    activeChains = picked;
  }
}
if (!activeChains.length) {
  activeChains = allChains;
}

// 3) 默认链：VUE_APP_DEFAULT_CHAIN > isDefault > 第一个启用链
const defaultChain =
  CHAIN_REGISTRY[process.env.VUE_APP_DEFAULT_CHAIN || ''] ||
  activeChains.find((c) => c.isDefault) ||
  activeChains[0];

/** 以十六进制 chainId 为 key 的网络表（兼容历史结构） */
const Network = buildNetworkMap(activeChains);

/** 链名 -> 十六进制 chainId */
const ChainIdMap = {};
/** 十六进制 chainId -> 链名 */
const ChainMapById = {};
/** 链名 -> 浏览器地址 */
const EtherScanMap = {};
activeChains.forEach((c) => {
  const hex = toHexChainId(c.chainId);
  ChainIdMap[c.chainName] = hex;
  ChainMapById[hex] = c.chainName;
  EtherScanMap[c.chainName] = c.explorer;
});

// 默认链的合约地址（兼容历史单链导出；多链场景请用 contractsOf()）
const Ticket_CONTRACT = defaultChain.contracts.ticket;
const TicketSale_CONTRACT = defaultChain.contracts.ticketSale;
const TicketMarket_CONTRACT = defaultChain.contracts.ticketMarket;
const Vault_CONTRACT = defaultChain.contracts.vault;
const PRDsClaim_CONTRACT = defaultChain.contracts.prdsClaim;
const BridgeAddr = defaultChain.contracts.bridge;

// 部署前缀（**不含 /api**）。它只被两处消费：
//   1) utils/request.js 作为 axios 的 baseURL；
//   2) issue.vue 的上传组件 `baseURL + '/api/upload'`。
// 而 api/index.js、api/signApi.js 里的 url **已经自带 '/api/...' 前缀**，
// 所以这里再拼一层 '/api' 会得到 /platform/api/api/xxx —— 后端无此路由，
// 落到 SPA 兜底（web.php 只注册 GET）后表现为 405。
//   部署在 /platform/ → baseURL='/platform'，最终请求 /platform/api/xxx，
//   由宿主机 nginx 剥掉 /platform 后转给容器（容器内仍是 /api/xxx）。
// 跨域独立 API 域名用 VUE_APP_API_BASE 覆盖（写 origin，不含 /api）。
const _base = process.env.BASE_URL || '/';
const baseURL = process.env.VUE_APP_API_BASE
  || (_base === '/' ? '' : _base.replace(/\/+$/, ''));

/** 默认链的代币列表（用于「随时增加 ERC20」的 UI 展示） */
const Tokens = defaultChain.tokens || [];

/** 取指定链（十六进制 chainId）的代币列表 */
function tokensOf(hexChainId) {
  const chain = activeChains.find((c) => toHexChainId(c.chainId) === hexChainId);
  return chain ? chain.tokens || [] : [];
}

/** 取指定链的合约地址集合 */
function contractsOf(hexChainId) {
  const chain = activeChains.find((c) => toHexChainId(c.chainId) === hexChainId);
  return chain ? chain.contracts : {};
}

/**
 * 当前选中链的合约地址（多链 v2.1.0）。
 * ---------------------------------------------------------------------------
 * 上面的 Ticket_CONTRACT / Vault_CONTRACT 等常量是「默认链」的历史导出，
 * 单链场景直接用没问题；一旦用户切到别的链，就必须按选中链取值，否则
 * allowance / approve / deposit 会打到默认链的合约上。
 *
 * 选中链存在 localStorage（与 store 的 desireChainId 同源），这里不依赖 Vuex，
 * 因此 sdk / 纯 js 模块也能拿到正确的地址。
 */
function currentContract(name, fallback = '') {
  let hex = '';
  try {
    hex = localStorage.getItem('predi-chain-id') || '';
  } catch (e) {
    hex = '';
  }
  const contracts = hex ? contractsOf(hex) : null;
  if (contracts && contracts[name] !== undefined) {
    return contracts[name];
  }
  return fallback;
}

export {
  ETHDecimal,
  ChainIdMap,
  ChainMapById,
  MaxUint256,
  EtherScanMap,
  Network,
  Ticket_CONTRACT,
  TicketSale_CONTRACT,
  Vault_CONTRACT,
  TicketMarket_CONTRACT,
  baseURL,
  BridgeAddr,
  PRDsClaim_CONTRACT,
  // 新增能力
  Tokens,
  tokensOf,
  contractsOf,
  currentContract,
  activeChains,
  defaultChain,
  toHexChainId,
};
